import { installationListReturnPath } from "@/lib/installationListNavigation";
import { useAuth } from "@/_core/hooks/useAuth";
import DashboardLayout from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { getInstallationContractDatePresentation } from "@/lib/installationContractDates";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { trpc } from "@/lib/trpc";
import {
  ArrowLeft,
  MapPin,
  Loader2,
  Building2,
  User,
  FileText,
  Hash,
} from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useLocation, useRoute, useSearch, Link } from "wouter";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import {
  buildCalendarReturnPath,
  parseCalendarContextParams,
} from "./calendarEventNavigation";
import {
  resolveInstallationTitle,
  resolveSiteLabel,
  resolveContractLabel,
} from "@/lib/installationContext";

// Sub-components
import { OverviewTab } from "@/components/installations/OverviewTab";
import { EvInstallationDossierTab } from "@/components/installations/EvInstallationDossierTab";
import { InstallationAddress } from "@/components/installations/InstallationAddress";
import { MaterialsTab } from "@/components/installations/MaterialsTab";
import { ReportsTab } from "@/components/installations/ReportsTab";
import { DocumentsTab } from "@/components/installations/DocumentsTab";
import { SignatureTab } from "@/components/installations/SignatureTab";
import { NotesTab } from "@/components/installations/NotesTab";
import { ContactsTab } from "@/components/installations/ContactsTab";
import { HistoryTab } from "@/components/installations/HistoryTab";
import TechniciansTab from "@/components/installations/TechniciansTab";
import { BillingTab } from "@/components/installations/BillingTab";
import { ContractInstallationBilling } from "@/components/installations/ContractInstallationBilling";
import { InstallationEconomicSummary } from "@/components/installations/InstallationEconomicSummary";
import { resolveEconomicMode } from "@/lib/installationContext";
import { formatCalendarDate } from "@/components/installations/contractAdjustmentsUtils";
import { REAL_COMPLETION_WITHOUT_VERIFIED_CLOSE_LABEL } from "./calendarUtils";

export default function InstallationDetail() {
  const { user } = useAuth();
  const [, setLocation] = useLocation();
  const [, params] = useRoute("/installations/:id");
  const installationId = params?.id ? parseInt(params.id) : 0;

  // CAL-07: only query with a valid numeric id; keep calendar context recoverable.
  const hasValidInstallationId = Number.isInteger(installationId) && installationId > 0;

  // State
  const detailSearch = useSearch();
  const queryParams = new URLSearchParams(detailSearch);
  const initialTab = queryParams.get("tab") || "overview";
  const initialSection = queryParams.get("section");
  const [activeTab, setActiveTab] = useState(initialTab);
  const utils = trpc.useUtils();

  // CAL-07: when the visit originated from the calendar, go back to the exact
  // calendar context (view, month/date, technician filter) instead of the list.
  const calendarContext = parseCalendarContextParams(detailSearch);
  const backPath = calendarContext ? buildCalendarReturnPath(calendarContext) : installationListReturnPath(detailSearch);

  // Queries
  const installationQuery = trpc.installations.getById.useQuery(
    { id: installationId },
    { enabled: hasValidInstallationId, retry: false },
  );

  // Mutations
  const updateMutation = trpc.installations.update.useMutation({
    onSuccess: () => {
      utils.installations.getById.invalidate({ id: installationId });
      utils.installationStatusHistory.list.invalidate({ installationId });
      toast.success("Estado actualizado");
    },
    onError: (err) => toast.error(err.message)
  });

  useEffect(() => {
    if (activeTab !== "billing" || initialSection !== "contract-adjustments") return;
    const timer = window.setTimeout(() => {
      document.getElementById("contract-adjustments")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 150);
    return () => window.clearTimeout(timer);
  }, [activeTab, initialSection, installationQuery.data]);

  // CAL-07: distinguish invalid id / FORBIDDEN / NOT_FOUND instead of always
  // claiming "Instalación no encontrada".
  if (!hasValidInstallationId) {
    return (
      <div className="p-8 text-center space-y-3">
        <p className="text-muted-foreground">Enlace de instalación no válido.</p>
        <Button variant="outline" onClick={() => setLocation(backPath)}>Volver</Button>
      </div>
    );
  }
  if (installationQuery.isLoading) return <div className="p-8 text-center flex justify-center"><Loader2 className="animate-spin w-8 h-8 text-muted-foreground" /></div>;
  const errorCode = installationQuery.error?.data?.code;
  if (errorCode === "FORBIDDEN") {
    return (
      <div className="p-8 text-center space-y-3">
        <p className="text-muted-foreground">No tienes permiso para ver esta instalación.</p>
        <Button variant="outline" onClick={() => setLocation(backPath)}>Volver</Button>
      </div>
    );
  }
  if (errorCode === "NOT_FOUND" || !installationQuery.data) {
    return (
      <div className="p-8 text-center space-y-3">
        <p className="text-muted-foreground">Instalación no encontrada</p>
        <Button variant="outline" onClick={() => setLocation(backPath)}>Volver</Button>
      </div>
    );
  }

  const inst = installationQuery.data;
  const canonicalCustomerId = Number((inst as any).canonicalCustomerPartnerId ?? (inst as any).contractCustomerPartnerId ?? 0);
  const canonicalCustomerName = (inst as any).canonicalCustomerName ?? null;
  const isManager = ['admin', 'project_manager', 'admin_manager'].includes(user?.role || '');

  // Status Badge Logic
  const statusColor: Record<string, string> = {
    pending: "bg-yellow-500",
    in_progress: "bg-blue-500",
    completed: "bg-green-500",
    cancelled: "bg-red-500"
  };

  const statusLabel: Record<string, string> = {
    pending: "Pendiente",
    in_progress: "Iniciada",
    completed: "Completada",
    cancelled: "Cancelada"
  };

  const badgeColor = statusColor[inst.status] || "bg-gray-500";
  const badgeLabel = statusLabel[inst.status] || "Estado registrado";

  return (
    <DashboardLayout>
      <div className="min-w-0 space-y-6 container mx-auto max-w-7xl">
        {/* Header Section */}
        <div className="flex flex-col md:flex-row md:flex-wrap min-w-0 items-start md:items-center justify-between gap-4 bg-card p-6 rounded-lg border shadow-sm">
          <div className="flex min-w-0 max-w-full w-full md:flex-[1_1_24rem] items-start gap-4">
            <Button aria-label="Volver al listado de origen" variant="ghost" size="icon" onClick={() => setLocation(backPath)}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="min-w-0 flex-1 [overflow-wrap:anywhere]">
              <div className="flex min-w-0 flex-wrap items-center gap-2 mb-1">
                <h1 className="min-w-0 max-w-full text-2xl font-bold tracking-tight">
                  {resolveInstallationTitle(inst)}
                </h1>
                <Badge className={cn("shrink-0 hover:bg-opacity-80", badgeColor)}>{badgeLabel}</Badge>
              </div>
              <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1 text-muted-foreground text-sm">
                {resolveSiteLabel(inst) && (
                  <span className="inline-flex min-w-0 max-w-full items-center gap-1">
                    <Building2 className="w-4 h-4 shrink-0" /> {resolveSiteLabel(inst)}
                  </span>
                )}
                <span className="inline-flex min-w-0 max-w-full items-center gap-1">
                  <MapPin className="w-4 h-4 shrink-0" /> <InstallationAddress installation={inst} />
                </span>
                <span className="inline-flex min-w-0 max-w-full items-center gap-1">
                  <User className="w-4 h-4 shrink-0" />
                {canonicalCustomerId > 0 ? (
                    <Link className="min-w-0 hover:underline underline-offset-2" href={`/customers/${canonicalCustomerId}?tab=contratos`}>
                      {canonicalCustomerName || "Cliente 360"}
                    </Link>
                  ) : (
                    "Cliente pendiente de resolver"
                  )}
                </span>
                {inst.workOrderType === "breakdown" && (inst as any).ticketReference && (
                  <div className="flex items-center gap-2"><Hash className="w-4 h-4 shrink-0" /> Ticket {(inst as any).ticketReference}</div>
                )}
                {inst.installationCode && (
                  <span className="inline-flex min-w-0 max-w-full items-center gap-1 font-mono text-xs">
                    <Hash className="w-4 h-4 shrink-0" /> {inst.installationCode}
                  </span>
                )}
                {resolveContractLabel(inst) && inst.contractId && canonicalCustomerId > 0 && (
                  <span className="inline-flex min-w-0 max-w-full items-center gap-1">
                    <FileText className="w-4 h-4 shrink-0" />
                    <Link
                      className="min-w-0 hover:underline underline-offset-2 font-mono text-xs"
                      href={`/customers/${canonicalCustomerId}/contracts/${inst.contractId}`}
                    >
                      {resolveContractLabel(inst)}
                      {inst.contractTitle ? ` · ${inst.contractTitle}` : ""}
                    </Link>
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* Status Change - Only for Managers */}
            {isManager ? (
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-muted-foreground">Estado:</span>
                <Select
                  disabled={updateMutation.isPending}
                  value={inst.status}
                  onValueChange={(val) => updateMutation.mutate({ id: inst.id, status: val as any })}
                >
                  <SelectTrigger className="w-[160px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pendiente</SelectItem>
                    <SelectItem value="in_progress">Iniciada</SelectItem>
                    <SelectItem value="completed">Completada</SelectItem>
                    <SelectItem value="cancelled">Cancelada</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div className="text-sm text-muted-foreground bg-muted px-3 py-1 rounded-full">
                Rol Técnico: Solo Lectura
              </div>
            )}
          </div>
        </div>
        {/* A-11: Dates Comparison Block */}
        {getInstallationContractDatePresentation(inst as any).showDateBlock && (
          <Card className="border shadow-sm">
            <CardContent className="py-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Planned dates */}
                <div className="space-y-1">
                  <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    Planificación {getInstallationContractDatePresentation(inst as any).legacyContractBadge && <Badge className="ml-1 bg-red-500 text-[10px]">Contractual</Badge>}
                  </h4>
                  <div className="text-sm">
                    <span className="text-muted-foreground">Inicio:</span>{' '}
                    {inst.startDate ? formatCalendarDate(inst.startDate) : '—'}
                  </div>
                  <div className="text-sm">
                    <span className="text-muted-foreground">Fin:</span>{' '}
                    {inst.endDate ? formatCalendarDate(inst.endDate) : '—'}
                  </div>
                </div>

                {getInstallationContractDatePresentation(inst as any).contractualDeadline && (
                  <div className="space-y-1" data-testid="installation-contract-deadline">
                    <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Compromiso contractual</h4>
                    <div className="text-sm"><span className="text-muted-foreground">Contrato:</span>{' '}{(inst as any).contractCode || `CTR-${(inst as any).contractId}`}</div>
                    <div className="text-sm"><span className="text-muted-foreground">Fecha límite:</span>{' '}{new Date(`${getInstallationContractDatePresentation(inst as any).contractualDeadline}T00:00:00`).toLocaleDateString('es-ES')}</div>
                  </div>
                )}

                {/* Real execution dates */}
                {((inst as any).realStartDate || (inst.status === 'completed' && !(inst as any).realEndDate)) && (
                  <div className="space-y-1">
                    <h4 className="text-xs font-semibold text-blue-600 uppercase tracking-wider">Ejecución Real</h4>
                    {(inst as any).realStartDate && <div className="text-sm">
                      <span className="text-muted-foreground">Inicio real:</span>{' '}
                      {new Date((inst as any).realStartDate).toLocaleDateString('es-ES')}
                    </div>}
                    <div className="text-sm">
                      {(inst as any).realEndDate
                        ? <><span className="text-muted-foreground">Fin real:</span>{' '}{new Date((inst as any).realEndDate).toLocaleDateString('es-ES')}</>
                        : inst.status === 'completed'
                          ? <><span className="text-muted-foreground">Estado:</span>{' '}{REAL_COMPLETION_WITHOUT_VERIFIED_CLOSE_LABEL}</>
                          : <><span className="text-muted-foreground">Fin real:</span>{' '}En curso</>}
                    </div>
                    {inst.endDate && (inst as any).realEndDate && (() => {
                      const planned = new Date(inst.endDate).getTime();
                      const actual = new Date((inst as any).realEndDate).getTime();
                      const diffDays = Math.round((actual - planned) / (1000 * 60 * 60 * 24));
                      if (diffDays === 0) return <div className="text-sm text-green-600 font-medium">En fecha</div>;
                      if (diffDays > 0) return <div className="text-sm text-orange-600 font-medium">+{diffDays} días de desviación</div>;
                      return <div className="text-sm text-green-600 font-medium">{Math.abs(diffDays)} días adelantada</div>;
                    })()}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Content Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="mb-4 flex h-auto w-full justify-start overflow-x-auto rounded-lg bg-muted/50 p-1">
            <TabsTrigger className="shrink-0 py-2" value="overview">Resumen</TabsTrigger>
            {inst.installationType === "ev_charger" && <TabsTrigger className="shrink-0 py-2" value="ev-dossier">Expediente EV</TabsTrigger>}
            <TabsTrigger className="shrink-0 py-2" value="documents">Documentos</TabsTrigger>
            <TabsTrigger className="shrink-0 py-2" value="reports">Partes Diarios</TabsTrigger>
            <TabsTrigger className="shrink-0 py-2" value="materials">Materiales</TabsTrigger>
            <TabsTrigger className="shrink-0 py-2" value="notes">Notas</TabsTrigger>
            <TabsTrigger className="shrink-0 py-2" value="contacts">Contactos</TabsTrigger>
            <TabsTrigger className="shrink-0 py-2" value="signature">Firma Cliente</TabsTrigger>
            {isManager && <TabsTrigger className="shrink-0 py-2" value="technicians">Técnicos</TabsTrigger>}
            {isManager && <TabsTrigger className="shrink-0 py-2" value="billing">Facturación</TabsTrigger>}
            <TabsTrigger className="shrink-0 py-2" value="history">Historial</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-0">
            <OverviewTab installation={inst} />
          </TabsContent>

          {inst.installationType === "ev_charger" && <TabsContent value="ev-dossier" className="mt-0">
            <EvInstallationDossierTab installation={inst} />
          </TabsContent>}

          <TabsContent value="documents" className="mt-0">
            <DocumentsTab installationId={inst.id} contractId={inst.contractId} contractDocumentId={inst.contractDocumentId} />
          </TabsContent>

          <TabsContent value="reports" className="mt-0">
            <ReportsTab installation={inst} />
          </TabsContent>

          <TabsContent value="materials" className="mt-0">
            <MaterialsTab installationId={inst.id} />
          </TabsContent>

          <TabsContent value="notes" className="mt-0">
            <NotesTab installationId={inst.id} />
          </TabsContent>

          <TabsContent value="contacts" className="mt-0">
            <ContactsTab
              installationId={inst.id}
              businessPartnerId={inst.primaryCustomerPartnerId ?? null}
            />
          </TabsContent>

          <TabsContent value="signature" className="mt-0">
            <SignatureTab installation={inst} refetch={installationQuery.refetch} />
          </TabsContent>

          {isManager && (
            <TabsContent value="technicians" className="mt-0">
              <TechniciansTab installationId={inst.id} assignedTechnicianIds={Array.isArray(inst.assignedTechnicianIds) ? (inst.assignedTechnicianIds as any[]).map(id => Number(id)) : []} />
            </TabsContent>
          )}

          {isManager && (
            <TabsContent value="billing" className="mt-0 space-y-4">
              {resolveEconomicMode(inst) === "contract" ? (
                <ContractInstallationBilling installation={inst} />
              ) : (
                <>
                  <InstallationEconomicSummary installation={inst} />
                  <BillingTab installation={inst} />
                </>
              )}
            </TabsContent>
          )}

          <TabsContent value="history" className="mt-0">
            <HistoryTab installationId={inst.id} />
          </TabsContent>

        </Tabs>
      </div>
    </DashboardLayout>
  );
}
